import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { Campo, Seletor } from './campos';
import { Modal } from './Modal';

export interface ClinicaParaJuntar {
  readonly id: string;
  readonly nome: string;
  readonly responsavel: string | null;
  readonly devedoresAtivos: number;
  readonly portal: { titular: string; compartilhadoCom: Array<{ id: string; nome: string }> } | null;
}

/**
 * Juntar duas clínicas num acesso só ao portal.
 *
 * O mesmo dentista responde por mais de uma razão social e não deve receber dois
 * links. Aqui ele passa a entrar uma vez e ver as duas carteiras.
 *
 * A tela existe porque a operação tem uma consequência que se perde numa caixa de
 * confirmação: o link da clínica que se junta DEIXA DE VALER. Se ele já foi enviado,
 * alguém vai abrir e não entrar mais. Por isso a consequência aparece escrita, com
 * o nome de quem perde o link, antes de o botão ficar disponível.
 */
export function ModalJuntarClinicas({
  aberto,
  aoFechar,
  destino,
  clinicas,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /** A clínica cujo link SOBREVIVE. */
  destino: ClinicaParaJuntar | null;
  /** Todas as clínicas, para escolher qual se junta. */
  clinicas: readonly ClinicaParaJuntar[];
}) {
  /* Por instância, não constante de módulo: dois diálogos iguais montados na mesma
     tela dividiriam o id, e o `form=` do botão do rodapé resolve pelo primeiro. */
  const ID_DO_FORMULARIO = useId();

  const clienteConsulta = useQueryClient();
  const [clinicaId, setClinicaId] = useState('');
  const [titular, setTitular] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  /* O titular sugerido é o responsável da clínica — quase sempre o dentista, que é
     o nome certo quando duas razões sociais passam a dividir a mesma entrada. */
  useEffect(() => {
    setClinicaId('');
    setErro(null);
    setTitular(destino?.responsavel ?? destino?.portal?.titular ?? destino?.nome ?? '');
  }, [destino, aberto]);

  const candidatas = clinicas.filter(
    (c) =>
      c.id !== destino?.id && !destino?.portal?.compartilhadoCom.some((x) => x.id === c.id),
  );
  const escolhida = candidatas.find((c) => c.id === clinicaId);

  const juntar = useMutation({
    mutationFn: () =>
      api.criar(`/clinicas/${destino?.id}/portal/agrupar`, {
        clinicaId,
        titular: titular.trim(),
      }),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['clinicas'] });
      aoFechar();
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível juntar as clínicas.'),
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    if (!clinicaId) {
      setErro('Escolha a clínica que vai dividir este acesso.');
      return;
    }
    juntar.mutate();
  }

  if (!destino) return null;

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Juntar clínicas num acesso só"
      descricao={`O link de ${destino.nome} passa a abrir as duas carteiras.`}
      largura="34rem"
      rodape={
        <>
          <Botao onClick={aoFechar} desabilitado={juntar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={juntar.isPending || !clinicaId}
          >
            {juntar.isPending ? 'Juntando…' : 'Juntar'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-5">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        {candidatas.length === 0 ? (
          <p className="text-sm text-[color:var(--texto-suave)]">
            Não há outra clínica para juntar a este acesso.
          </p>
        ) : (
          <>
            <Seletor
              rotulo="Clínica que passa a usar este acesso"
              valor={clinicaId}
              aoMudar={(v) => {
                setClinicaId(v);
                setErro(null);
              }}
              vazio="Selecione…"
              opcoes={candidatas.map((c) => ({
                valor: c.id,
                rotulo: `${c.nome} — ${c.devedoresAtivos} devedores`,
              }))}
            />

            <Campo
              rotulo="Nome que aparece no topo do portal"
              valor={titular}
              aoMudar={setTitular}
              placeholder="Dr. Álvaro Peixoto"
              auxilio="Com duas clínicas, o nome do dentista diz mais que o de qualquer uma delas."
            />

            {/* A consequência ANTES do botão, e com nome e sobrenome. É o que faz
                a diferença entre decidir e descobrir depois. */}
            {escolhida && (
              <div className="rounded-md border p-4 text-sm">
                <p className="text-[color:var(--texto)]">O que acontece:</p>
                <ul className="mt-2 space-y-1.5 text-[color:var(--texto-suave)]">
                  <li>
                    · Quem entrar pelo link de <strong>{destino.nome}</strong> passa a ver as
                    duas carteiras, somadas.
                  </li>
                  <li>
                    · Cada cliente continua mostrando de qual clínica é — no sistema elas
                    seguem separadas, como o judicial exige.
                  </li>
                  <li style={{ color: 'var(--color-atencao)' }}>
                    · O link de <strong>{escolhida.nome}</strong> deixa de valer. Se você já
                    o enviou, avise que agora é o outro.
                  </li>
                </ul>
              </div>
            )}
          </>
        )}
      </form>
    </Modal>
  );
}
